import React, { useCallback, useEffect, useState } from 'react';
import * as hr from '../../../api';
import { Select } from '../../../components/Controls';

// HR Request Center > Advanced: shared tones, formats, the meta loader and small form parts (Stephanet's hrrc/adv/advShared.jsx).
// The server speaks the labels the screens show ("Pending Approval", "Cost Request" …).

export const ADV_TONE = {
  Draft: 'gray', Submitted: 'blue', 'Under Review': 'purple', 'Pending Approval': 'yellow', 'Returned for Correction': 'orange', Approved: 'green',
  Rejected: 'red', Cancelled: 'gray', Closed: 'teal', 'In Progress': 'indigo', Completed: 'green', 'Pending Finance': 'orange',
  'Pending Procurement': 'blue', 'Not Required': 'gray', 'Not Assigned': 'gray', Assigned: 'indigo', Overdue: 'red',
  Emergency: 'darkred', High: 'orange', Urgent: 'red', Medium: 'yellow', Low: 'green', 'Pending Retirement': 'orange', 'Fully Retired': 'green',
  'Awaiting Documents': 'yellow', 'Awaiting Vendor': 'yellow', 'Awaiting Payment': 'yellow', Pending: 'yellow',
};
export const AChip = ({ text }) => (text ? <span className={`mdr-chip t-${ADV_TONE[text] || 'gray'}`}>{text}</span> : <span className="mds-dash">—</span>);

export const ngn = v => `₦${Number(v || 0).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`;
export const shortM = v => (v >= 1e9 ? `₦${(v / 1e9).toFixed(1)}B` : `₦${(v / 1e6).toFixed(1)}M`);
export const num = v => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);
export const yn = v => (v ? 'Yes' : 'No');
export const rid = () => Math.random().toString(36).slice(2);

// Section D row total — the same formula as the server (adv_request_service.calculate_total)
const first = (row, keys) => { for (const k of keys) { const v = num(row[k]); if (v) return v; } return 0; };
export function calcTotal(row, formula) {
  const qty = first(row, ['quantity', 'quantity_required', 'litres_required', 'area_sqm', 'number_of_pages']);
  const rate = first(row, ['unit_price', 'rate_per_litre', 'rate_per_night', 'rate_per_day', 'daily_rate', 'overtime_rate', 'daily_transport_rate', 'rate', 'unit_rate', 'monthly_rate']);
  const persons = first(row, ['number_of_persons', 'number_of_guards', 'number_of_officers']);
  const days = first(row, ['number_of_days', 'total_days', 'number_of_nights', 'total_hours']);
  const gross = first(row, ['gross_salary', 'amount_due', 'invoice_amount', 'work_done_value', 'contract_sum', 'current_amount', 'quantity_available']);
  const ded = first(row, ['deductions', 'amount_paid', 'retention', 'quantity_issued']);
  const copies = first(row, ['number_of_copies']) || 1;
  switch (formula) {
    case 'qty * rate': return qty * rate;
    case 'persons * days * rate': return persons * days * rate;
    case 'nights * rate': case 'days * rate': return days * rate;
    case 'gross - deductions': return Math.max(0, gross - ded);
    case 'gross + rate': return gross + first(row, ['adjustment_amount']);
    case 'pages * copies * rate': return qty * copies * rate;
    case 'rate': return rate;
    default: return 0;
  }
}
export const calcField = fields => fields.find(f => f.kind === 'calculated' || f.key === 'total_amount');
export const rowTotal = (fields, row) => { const c = calcField(fields); return c ? (c.formula ? calcTotal(row, c.formula) : 0) || num(row.total_amount) : 0; };

// lookups in the meta
export const detailTypeOf = (meta, id) => meta.units.flatMap(u => u.detail_types).find(d => String(d.id) === String(id));
export const unitOf = (meta, id) => meta.units.find(u => String(u.id) === String(id));
export const nameOf = (list, id) => (list || []).find(x => String(x.id) === String(id))?.name;

let cache = null;
export function useAdvMeta() {
  const [meta, setMeta] = useState(cache);
  const [error, setError] = useState('');
  const reload = useCallback(() => hr.advMeta().then(m => { cache = m; setMeta(m); return m; }).catch(e => setError(e.message)), []);
  useEffect(() => { reload(); }, [reload]);
  return { meta, error, reload };
}

export const Field = ({ label, req, children, span, err }) => (
  <label className={`hrc-f ${span ? `span${span}` : ''}`}>
    <span>{label}{req && <b className="hrc-req"> *</b>}</span>{children}{err && <p className="hrc-err">{err}</p>}
  </label>
);

// a select of plain labels, or of {id, name} rows (value = id)
export function Pick({ value, onChange, list, ph = 'Select...', ids, disabled, bad }) {
  return (
    <Select value={value ?? ''} disabled={disabled} className={bad ? 'bad' : ''} onChange={e => onChange(e.target.value)}>
      <option value="">{ph}</option>
      {(list || []).map(x => (ids ? <option key={x.id} value={x.id}>{x.name}</option> : <option key={x} value={x}>{x}</option>))}
    </Select>
  );
}

export function Section({ title, sub, Ico, open, onToggle, children, tone = '' }) {
  return (
    <div className={`adv-sec ${tone}`}>
      <button type="button" className="adv-sec-head" onClick={onToggle}>
        <span className="adv-sec-title">{Ico && <Ico size={16} />}<span><b>{title}</b>{sub && <small>{sub}</small>}</span></span>
        <span className="adv-sec-chev">{open ? '▴' : '▾'}</span>
      </button>
      {open && <div className="adv-sec-body">{children}</div>}
    </div>
  );
}

export function downloadCsv(name, header, rows) {
  const escCsv = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const text = [header.map(escCsv).join(','), ...rows.map(r => r.map(escCsv).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  a.download = name;
  a.click();
}

// Inventory stock, the asset register and the fleet register are not built yet (the pickers say so)
export const NOT_YET = 'Not available yet — it comes when the module is built.';
